// =============================================================================
// VandieZ agent-web -- Vehicles (/vehicles)
// =============================================================================
// Lists vehicles belonging to the currently-selected owner. Agents can view
// fleet status and navigate to individual vehicle detail pages.
//
// Adapted from shipper-web/src/pages/VehiclesPage.tsx:
//   * vehiclesApi.list now takes currentOwnerId as the second argument.
//   * Guard includes currentOwnerId === null (owner not yet resolved).
//   * Smartphone "Add vehicle" banner removed; replaced with owner-scoped note.
//   * Uses AppHeader (which already renders the owner switcher dropdown).
// =============================================================================

import {
  CheckCircle2,
  Circle,
  Loader2,
  Truck,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { vehiclesApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { ApiError } from "@/types/auth";
import type { Vehicle, VehicleStatus } from "@/types/vehicle";

const STATUS_LABEL: Record<VehicleStatus, string> = {
  pending: "Pending review",
  active: "Active",
  rejected: "Rejected",
  maintenance: "Maintenance",
  suspended: "Suspended",
};

const STATUS_TONE: Record<VehicleStatus, string> = {
  pending: "bg-warning-subtle text-warning-subtle-foreground",
  active: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-danger-subtle text-danger-subtle-foreground",
  maintenance: "bg-muted text-muted-foreground",
  suspended: "bg-danger-subtle text-danger-subtle-foreground",
};

export function VehiclesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const { currentOwnerId, currentOwner } = useOwnerContext();
  const [items, setItems] = useState<Vehicle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (accessToken === null || currentOwnerId === null) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    vehiclesApi
      .list(accessToken, currentOwnerId, { limit: 50, offset: 0 }, controller.signal)
      .then((res) => {
        setItems(res.items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        if (err instanceof ApiError) {
          const code = err.problem.code;
          if (code === "AGENT_PERMISSION_MISSING") {
            setError("Ask the owner to grant the 'vehicles:read' permission on 'vehicles'.");
          } else if (code === "AGENT_NO_GRANT_FOR_OWNER") {
            setError("Your access to this owner was revoked. Switch owner via the header dropdown.");
          } else if (err.problem.status === 422 && code === "AGENT_OWNER_PARAM_REQUIRED") {
            setError("No owner selected — switch via the header dropdown.");
          } else {
            setError(err.problem.detail);
          }
        } else {
          setError("Couldn't load vehicles.");
        }
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, currentOwnerId]);

  if (currentOwnerId === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Vehicles" />
        <main className="mx-auto max-w-4xl px-6 py-16 text-center text-muted-foreground">
          No owner selected — head to{" "}
          <Link to="/" className="font-medium text-primary underline underline-offset-2">
            My Owners
          </Link>{" "}
          and pick one.
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <AppHeader title="Vehicles" />

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        {error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items === null || items.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Truck className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No vehicles yet</CardTitle>
              <CardDescription>
                {currentOwner !== null
                  ? `${currentOwner.owner_full_name}'s fleet has no vehicles yet. The owner can onboard new vehicles via the mobile app.`
                  : "This owner's fleet has no vehicles yet. The owner can onboard new vehicles via the mobile app."}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((v) => (
              <Link
                key={v.id}
                to={`/vehicles/${v.id}?as=${currentOwnerId}`}
                className="block transition-colors hover:opacity-95"
              >
                <Card className="group cursor-pointer transition-colors hover:border-primary/50">
                  <CardContent className="flex items-center gap-4 py-4">
                    {v.primary_photo_url !== null ? (
                      <img
                        src={v.primary_photo_url}
                        alt={`${v.make} ${v.model}`}
                        className="h-16 w-24 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-24 items-center justify-center rounded-md bg-muted">
                        <Truck className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[v.status]}`}
                        >
                          {STATUS_LABEL[v.status]}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {v.vehicle_type} · {v.fuel_type}
                        </span>
                      </div>
                      <p className="text-sm font-medium">
                        {v.make} {v.model} · {v.year_of_manufacture}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {v.registration_number} · {v.payload_capacity_kg} kg
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="flex items-center justify-end gap-1 text-xs">
                        {v.online_for_bidding ? (
                          <>
                            <CheckCircle2 className="h-3 w-3 text-success" />
                            <span className="text-success-subtle-foreground">Online</span>
                          </>
                        ) : (
                          <>
                            <Circle className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Offline</span>
                          </>
                        )}
                      </p>
                      {v.vahan_verified ? (
                        <p className="text-xs text-muted-foreground">
                          Vahan ✓
                        </p>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
